import { AdminLayout } from "@/components/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Trash2, HardDrive, FileAudio, Clock, TrendingDown } from "lucide-react";
import { useState } from "react";
import { format } from "date-fns";

const StorageStats = () => {
  const [isRunningCleanup, setIsRunningCleanup] = useState(false);

  // Cleanup logs from audio_processing_logs where step = 'demo_cleanup'
  const { data: cleanupLogs, refetch: refetchLogs } = useQuery({
    queryKey: ["storage-cleanup-logs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("audio_processing_logs")
        .select("*")
        .eq("step", "demo_cleanup")
        .order("created_at", { ascending: false })
        .limit(30);
      return data || [];
    },
  });

  // Active demos (still have demo_url, not purchased)
  const { data: activeDemos, refetch: refetchActive } = useQuery({
    queryKey: ["storage-active-demos"],
    queryFn: async () => {
      const { data, count } = await supabase
        .from("generations")
        .select("id, created_at, demo_expires_at", { count: "exact" })
        .not("demo_url", "is", null)
        .eq("is_full_purchased", false);
      return { items: data || [], count: count || 0 };
    },
  });

  // Expired demos pending cleanup
  const { data: expiredCount } = useQuery({
    queryKey: ["storage-expired-demos"],
    queryFn: async () => {
      const { count } = await supabase
        .from("generations")
        .select("id", { count: "exact", head: true })
        .not("demo_url", "is", null)
        .eq("is_full_purchased", false)
        .not("demo_expires_at", "is", null)
        .lt("demo_expires_at", new Date().toISOString());
      return count || 0;
    },
  });

  // Total cleaned up (sum of file_size field which stores deletedCount)
  const totalCleaned = cleanupLogs?.reduce((sum, log) => sum + (log.file_size || 0), 0) || 0;

  // Estimate storage saved (avg demo ~500KB, 2 versions per generation)
  const estimatedSavedMB = ((totalCleaned * 2 * 500) / 1024).toFixed(1);

  // Demos that will expire in next hour
  const { data: expiringCount } = useQuery({
    queryKey: ["storage-expiring-soon"],
    queryFn: async () => {
      const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      const { count } = await supabase
        .from("generations")
        .select("id", { count: "exact", head: true })
        .not("demo_url", "is", null)
        .eq("is_full_purchased", false)
        .not("demo_expires_at", "is", null)
        .lt("demo_expires_at", oneHourFromNow)
        .gt("demo_expires_at", new Date().toISOString());
      return count || 0;
    },
  });

  // Total demos ever deleted (from DB: generations with no demo_url and not purchased)
  const { data: totalDeletedCount } = useQuery({
    queryKey: ["storage-total-deleted"],
    queryFn: async () => {
      const { count } = await supabase
        .from("generations")
        .select("id", { count: "exact", head: true })
        .is("demo_url", null)
        .eq("is_full_purchased", false)
        .not("demo_expires_at", "is", null);
      return count || 0;
    },
  });

  const runCleanup = async () => {
    setIsRunningCleanup(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const resp = await fetch(`https://${projectId}.supabase.co/functions/v1/cleanup-demo-audio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const result = await resp.json();
      console.log("Cleanup result:", result);
      setTimeout(() => {
        refetchLogs();
        refetchActive();
        setIsRunningCleanup(false);
      }, 2000);
    } catch {
      setIsRunningCleanup(false);
    }
  };

  return (
    <AdminLayout title="Storage" description="Статистика хранилища и очистка демо-файлов">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">💾 Storage Manager</h1>
          <Button onClick={runCleanup} disabled={isRunningCleanup} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${isRunningCleanup ? "animate-spin" : ""}`} />
            {isRunningCleanup ? "Очистка..." : "Запустить очистку"}
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <FileAudio className="w-6 h-6 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold">{activeDemos?.count || 0}</p>
              <p className="text-xs text-muted-foreground">Активных демо</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <Clock className="w-6 h-6 text-orange-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-orange-500">{expiredCount || 0}</p>
              <p className="text-xs text-muted-foreground">Ожидают удаления</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <TrendingDown className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-yellow-500">{expiringCount || 0}</p>
              <p className="text-xs text-muted-foreground">Истекут через 1ч</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <Trash2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-500">{totalDeletedCount || 0}</p>
              <p className="text-xs text-muted-foreground">Всего удалено</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <HardDrive className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-500">{estimatedSavedMB} MB</p>
              <p className="text-xs text-muted-foreground">~Освобождено</p>
            </CardContent>
          </Card>
        </div>

        {/* Storage breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">📊 Текущее состояние</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">Активные демо-файлы</span>
                <Badge variant="secondary">{(activeDemos?.count || 0) * 2} файлов</Badge>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">~Размер активных демо</span>
                <Badge variant="secondary">{(((activeDemos?.count || 0) * 2 * 500) / 1024).toFixed(1)} MB</Badge>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">Cron-очистка</span>
                <Badge className="bg-green-500/10 text-green-500 border-green-500/20">каждые 30 мин</Badge>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">TTL демо</span>
                <Badge variant="outline">24 часа</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">🛡️ Защита</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">Оплаченные треки</span>
                <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Не удаляются</Badge>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">Финальные версии</span>
                <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Бессрочно</Badge>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">Метаданные (текст, история)</span>
                <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Сохраняются</Badge>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                <span className="text-sm">Удаление при покупке</span>
                <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Немедленно</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cleanup History */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">🗑️ История очисток</CardTitle>
          </CardHeader>
          <CardContent>
            {!cleanupLogs || cleanupLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Нет записей об очистке</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Время</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Удалено</TableHead>
                      <TableHead>~Освобождено</TableHead>
                      <TableHead>Ошибки</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cleanupLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-xs font-mono">
                          {format(new Date(log.created_at), "dd.MM.yyyy HH:mm")}
                        </TableCell>
                        <TableCell>
                          {log.status === "success" ? (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">✓ OK</Badge>
                          ) : log.status === "partial" ? (
                            <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">⚠ Partial</Badge>
                          ) : (
                            <Badge variant="destructive">✗ Error</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{log.file_size || 0} генераций</TableCell>
                        <TableCell className="text-sm">
                          ~{(((log.file_size || 0) * 2 * 500) / 1024).toFixed(1)} MB
                        </TableCell>
                        <TableCell className="text-xs text-destructive">
                          {log.error_message || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default StorageStats;
